/**
 * Sidebar secundario del módulo Comercial + Outlet.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useLocation, useNavigate, Outlet } from 'react-router-dom';
import {
  Box, Typography, List, ListItemButton, ListItemIcon, ListItemText,
  Tabs, Tab, Chip, Badge,
} from '@mui/material';
import HistoryIcon from '@mui/icons-material/History';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import BusinessIcon from '@mui/icons-material/Business';
import DashboardIcon from '@mui/icons-material/Dashboard';
import AddTaskIcon from '@mui/icons-material/AddTask';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ScheduleIcon from '@mui/icons-material/Schedule';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import { API_BASE, getEmpresaId, getAuthHeaders } from './format';

// Menú Comercial — visible solo las opciones activas por ahora. El resto
// (Pendientes, Alertas, Bitácora, Prospectos, Cartera, Dashboard) están
// conservadas como comentario para reactivar sin re-buscar iconos/paths.
const SECTIONS = [
  { path: 'ventas',     label: 'Venta diaria', icon: <ShoppingCartIcon />,  color: '#16a34a' },
  { path: 'lag',        label: 'Lag',         icon: <ScheduleIcon />,      color: '#7c3aed' },
  // { path: 'pendientes', label: 'Pendientes',  icon: <AddTaskIcon />,       color: '#dc2626', badgeKey: 'pendientes' },
  // { path: 'alertas',    label: 'Alertas',     icon: <WarningAmberIcon />,  color: '#f59e0b', badgeKey: 'alertas' },
  // { path: 'visitas',    label: 'Bitácora',    icon: <HistoryIcon />,       color: '#0d9488' },
  // { path: 'prospectos', label: 'Prospectos',  icon: <PersonSearchIcon />,  color: '#8b5cf6' },
  // { path: 'cartera',    label: 'Mi cartera',  icon: <BusinessIcon />,      color: '#0ea5e9' },
  // { path: 'dashboard',  label: 'Dashboard',   icon: <DashboardIcon />,     color: '#3b82f6' },
];

export default function ComercialLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const [counts, setCounts] = useState({ pendientes: 0, alertas: 0 });

  const cargarCounts = useCallback(async () => {
    try {
      const empresaId = getEmpresaId();
      const [pend, alert] = await Promise.all([
        fetch(`${API_BASE}/turismo/comercial/pendientes?empresa_id=${empresaId}`,
          { headers: getAuthHeaders() }).then(r => r.ok ? r.json() : null),
        fetch(`${API_BASE}/turismo/comercial/alertas/count?empresa_id=${empresaId}`,
          { headers: getAuthHeaders() }).then(r => r.ok ? r.json() : null),
      ]);
      setCounts({
        pendientes: (pend?.totales?.atrasados || 0) + (pend?.totales?.hoy || 0),
        alertas: alert?.pendientes || 0,
      });
    } catch {}
  }, []);
  useEffect(() => {
    cargarCounts();
    const t = setInterval(cargarCounts, 60_000);  // refresca cada minuto
    return () => clearInterval(t);
  }, [cargarCounts]);

  useEffect(() => {
    // Default: primera sección visible (Venta diaria). Al reactivar más
    // opciones basta con cambiar el orden en SECTIONS y esto seguirá válido.
    if (location.pathname === '/comercial' || location.pathname === '/comercial/') {
      navigate(`/comercial/${SECTIONS[0].path}`, { replace: true });
    }
  }, [location.pathname, navigate]);

  return (
    <Box sx={{ display: 'flex', height: 'calc(100vh - 64px)', bgcolor: '#f8fafc' }}>
      {/* Sidebar secundario (desktop) */}
      <Box sx={{
        width: 220, flexShrink: 0, bgcolor: '#fff',
        borderRight: '1px solid #e2e8f0',
        display: { xs: 'none', md: 'flex' }, flexDirection: 'column',
      }}>
        <Box sx={{ p: 2.5, pb: 1.5 }}>
          <Typography sx={{ fontSize: 11, color: '#94a3b8',
                            textTransform: 'uppercase', letterSpacing: 1, fontWeight: 700 }}>
            Comercial / CRM
          </Typography>
        </Box>
        <List sx={{ px: 1, py: 0 }} dense>
          {SECTIONS.map(s => {
            const isActive = location.pathname.includes(`/comercial/${s.path}`);
            const c = s.badgeKey ? counts[s.badgeKey] : 0;
            return (
              <ListItemButton key={s.path} onClick={() => navigate(`/comercial/${s.path}`)}
                sx={{
                  borderRadius: 2, mb: 0.5, py: 0.75, pl: 1.5,
                  bgcolor: isActive ? `${s.color}15` : 'transparent',
                  borderLeft: isActive ? `3px solid ${s.color}` : '3px solid transparent',
                  '&:hover': { bgcolor: isActive ? `${s.color}25` : '#f1f5f9' },
                }}>
                <ListItemIcon sx={{ minWidth: 32, color: isActive ? s.color : '#94a3b8' }}>
                  {s.icon}
                </ListItemIcon>
                <ListItemText primary={s.label}
                  primaryTypographyProps={{
                    fontSize: 13, fontWeight: isActive ? 700 : 500,
                    color: isActive ? '#0f172a' : '#475569',
                  }} />
                {c > 0 && (
                  <Chip size="small" label={c}
                    sx={{ height: 18, fontSize: 10, fontWeight: 700,
                          bgcolor: s.color, color: '#fff' }} />
                )}
              </ListItemButton>
            );
          })}
        </List>
      </Box>

      {/* Contenido */}
      <Box sx={{ flex: 1, overflow: 'auto', p: { xs: 2, md: 3 } }}>
        {/* Tabs móviles */}
        <Box sx={{ display: { xs: 'block', md: 'none' }, mb: 2,
                    mx: -2, px: 2, borderBottom: '1px solid #e2e8f0',
                    bgcolor: '#fff', position: 'sticky', top: -16, zIndex: 5 }}>
          <Tabs
            value={Math.max(0, SECTIONS.findIndex(s => location.pathname.includes(`/comercial/${s.path}`)))}
            onChange={(_, i) => navigate(`/comercial/${SECTIONS[i].path}`)}
            variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile
            sx={{ minHeight: 44,
                  '& .MuiTab-root': { minHeight: 44, textTransform: 'none',
                                       fontWeight: 700, fontSize: 12, px: 1.5 },
                  '& .MuiTabs-indicator': { backgroundColor: '#0d9488',
                                              height: 3, borderRadius: 2 } }}>
            {SECTIONS.map(s => {
              const c = s.badgeKey ? counts[s.badgeKey] : 0;
              return (
                <Tab key={s.path} icon={s.icon} iconPosition="start"
                  label={
                    <Badge badgeContent={c} max={99}
                      sx={{ '& .MuiBadge-badge': { bgcolor: s.color, color: '#fff',
                                                     fontSize: 9, height: 16, minWidth: 16,
                                                     right: -6, top: 2 } }}>
                      <span style={{ paddingRight: c > 0 ? 10 : 0 }}>{s.label}</span>
                    </Badge>
                  } />
              );
            })}
          </Tabs>
        </Box>

        <Outlet />
      </Box>
    </Box>
  );
}
